import { useEffect, useState } from "react";
import { Loader2, Download, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { inp, money } from "./inputs";

const iso = (d) => d.toISOString().slice(0, 10);
const PERIODS = [
  ["today", "Today"], ["week", "This Week"], ["month", "This Month"], ["year", "This Year"], ["all", "All Time"],
];

function range(period) {
  const now = new Date();
  if (period === "today") return [iso(now), iso(now)];
  if (period === "week") { const d = new Date(now); d.setDate(d.getDate() - d.getDay()); return [iso(d), iso(now)]; }
  if (period === "month") return [iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(now)];
  if (period === "year") return [iso(new Date(now.getFullYear(), 0, 1)), iso(now)];
  return ["", ""];
}

export const ReportsTab = () => {
  const [period, setPeriod] = useState("month");
  const [from, setFrom] = useState(range("month")[0]);
  const [to, setTo] = useState(range("month")[1]);
  const [data, setData] = useState(null);

  const load = async (f, t) => {
    setData(null);
    try {
      const { data } = await api.get("/admin/reports/summary", { params: { date_from: f || undefined, date_to: t || undefined } });
      setData(data);
    } catch { toast.error("Failed to load report"); }
  };
  useEffect(() => { load(from, to); /* eslint-disable-next-line */ }, []);

  const pick = (p) => { setPeriod(p); const [f, t] = range(p); setFrom(f); setTo(t); load(f, t); };
  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/reports/export.csv", { params: { date_from: from || undefined, date_to: to || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "one-love-sales-report.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const Stat = ({ label, value, red, green }) => (
    <div className="bg-white/[0.03] border border-white/10 rounded-lg p-4">
      <p className="text-xs text-white/40 uppercase tracking-wider">{label}</p>
      <p className={`font-display font-bold text-2xl ${red ? "text-[#E63946]" : green ? "text-emerald-400" : ""}`}>{value}</p>
    </div>
  );

  return (
    <div data-testid="reports-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-display font-bold text-2xl">Financial Reports</h1>
        <button onClick={exportCsv} data-testid="export-csv-btn" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm hover:bg-white/5"><Download size={15} /> Export CSV</button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {PERIODS.map(([v, l]) => (
          <button key={v} onClick={() => pick(v)} className={`px-4 py-2 rounded-full text-sm transition-colors ${period === v ? "bg-white text-[#0A0A0A]" : "border border-white/15 text-white/60 hover:border-white/40"}`}>{l}</button>
        ))}
        <div className="flex gap-2 items-center ml-auto">
          <input type="date" className={`${inp} w-auto`} value={from} onChange={(e) => setFrom(e.target.value)} />
          <input type="date" className={`${inp} w-auto`} value={to} onChange={(e) => setTo(e.target.value)} />
          <button onClick={() => { setPeriod("custom"); load(from, to); }} className="rounded-full border border-white/15 px-4 py-2 text-sm hover:bg-white/5">Apply</button>
        </div>
      </div>
      {!data ? <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#E63946]" /></div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="report-stats">
            <Stat label="Gross Sales" value={money(data.gross_sales)} />
            <Stat label="Orders" value={data.orders} />
            <Stat label="Ticket Revenue" value={money(data.ticket_revenue)} />
            <Stat label="Merch Revenue" value={money(data.merch_revenue)} />
            <Stat label="Discounts Given" value={`-${money(data.discounts_given)}`} red />
            <Stat label="Service Fees" value={money(data.service_fees_collected)} />
            <Stat label="Sales Tax Collected" value={money(data.sales_tax_collected)} />
            <Stat label="Shipping Collected" value={money(data.shipping_collected)} />
            <Stat label="Refunds" value={`-${money(data.refunds)}`} red />
            <Stat label="Est. Net Proceeds" value={money(data.net_proceeds)} green />
          </div>
          <p className="text-xs text-white/35 mb-6">Net proceeds = gross sales − refunds − sales tax (tax collected belongs to the state, not the church). Stripe/PayPal processing fees are deducted by the processors and visible in their dashboards.</p>

          {Object.keys(data.promo_usage || {}).length > 0 && (
            <div className="bg-white/[0.03] border border-white/10 rounded-lg p-5 mb-6">
              <p className="text-xs uppercase tracking-wider text-white/40 mb-3">Promo Code Usage (this period)</p>
              {Object.entries(data.promo_usage).map(([code, n]) => (
                <p key={code} className="text-sm py-1"><span className="font-mono font-bold">{code}</span> <span className="text-white/40">— used {n} time{n > 1 ? "s" : ""}</span></p>
              ))}
            </div>
          )}

          <div className="bg-white/[0.03] border border-[#E63946]/30 rounded-lg p-5" data-testid="donations-separate">
            <p className="text-xs uppercase tracking-wider text-[#E63946] mb-2 flex items-center gap-2"><HeartHandshake size={14} /> Giving (Tracked Separately)</p>
            <p className="font-display font-bold text-2xl">{money(data.donations_separate?.total)} <span className="text-white/30 text-base font-normal">· {data.donations_separate?.count} gifts</span></p>
            <p className="text-xs text-white/35 mt-1">{data.donations_separate?.note}</p>
          </div>
        </>
      )}
    </div>
  );
};
